import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * InvoiceOrderDialog — sevk edilmiş miktarların faturalanması.
 * Kaynak siparişin aşaması DEĞİŞMEZ; ayrı FT-xxx fatura belgesi oluşur.
 * Kalem bazlı: faturalanabilir = sevk edilen − daha önce faturalanan.
 * İrsaliye & Fatura ve sonrası aşamalarda sevk kaydı atlanmış olabilir —
 * o durumda faturalanabilir = kalem adedi − faturalanan (backend ile aynı kural).
 * Tarihler: Fatura Tarihi (varsayılan bugün) + Vade Tarihi (varsayılan sipariş
 * vadesi); vade < fatura tarihi olamaz (frontend uyarır + backend 422).
 */
const DISPATCH_STAGES = ["irsaliye_fatura", "sevkiyat", "tamamlandi"];

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function InvoiceOrderDialog({ open, onOpenChange, order, onSaved }) {
  const [qtys, setQtys] = useState({});
  const [priceDrafts, setPriceDrafts] = useState({}); // FT'ye özel birim fiyat (yalnız değiştirilenler)
  const [invDate, setInvDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [saving, setSaving] = useState(false);

  const lines = useMemo(() => {
    if (!order) return [];
    const li = order.line_items || [];
    if (li.length) {
      return li.map((ln) => ({
        id: ln.id,
        name: ln.product_name,
        sku: ln.variant_sku || "",
        quantity: Number(ln.quantity || 0),
        shipped: Number(ln.shipped_qty || 0),
        invoiced: Number(ln.invoiced_qty || 0),
        // Tutar özeti için sipariş satırı snapshot'ı (REUSE — yeniden girilmez)
        unit_price: Number(ln.unit_price || 0),
        currency: (ln.currency || "TRY").toUpperCase(),
        tax_rate: Number(ln.tax_rate || 0),
        discount_percent: Number(ln.discount_percent || 0),
        discount_amount: Number(ln.discount_amount || 0),
      }));
    }
    // Legacy (satırsız) sipariş — sipariş seviyesi takip (backend legacy kuralı: KDV %10)
    return [{
      id: "",
      name: order.product_name,
      sku: order.variant_sku || "",
      quantity: Number(order.quantity || 0),
      shipped: Number(order.shipped_total || 0),
      invoiced: Number(order.invoiced_total || 0),
      unit_price: Number(order.unit_price || 0),
      currency: "TRY",
      tax_rate: 10,
      discount_percent: 0,
      discount_amount: 0,
    }];
  }, [order]);

  const keyOf = (l) => l.id || "_legacy";
  const remainingOf = (l) => {
    const base = DISPATCH_STAGES.includes(order?.stage) ? l.quantity : l.shipped;
    return Math.max(0, base - l.invoiced);
  };

  useEffect(() => {
    if (open) {
      const init = {};
      lines.forEach((l) => { init[keyOf(l)] = remainingOf(l); });
      setQtys(init);
      setPriceDrafts({});
      setInvDate(todayLocal());
      setDueDate(String(order?.due_date || "").slice(0, 10));
    }
  }, [open, lines]);

  const totalQty = lines.reduce((s, l) => s + Number(qtys[keyOf(l)] || 0), 0);

  // Satır tutar hesabı — sipariş satırı snapshot'ından oransal (alt toplamla BİREBİR
  // aynı formül; backend invoice_order_shipped ile uyumlu). q = bu faturadaki adet.
  // Birim fiyat düzenlenebilir: değiştirildiyse FT'ye özel fiyat hesaba girer,
  // kaynak sipariş satırı ETKİLENMEZ.
  const fmtN = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const parsePrice = (v) => {
    const n = parseFloat(String(v ?? "").replace(/\s/g, "").replace(",", "."));
    return Number.isNaN(n) || n < 0 ? null : n;
  };
  const priceOf = (l) => {
    const d = priceDrafts[keyOf(l)];
    if (d === undefined) return l.unit_price;
    const p = parsePrice(d);
    return p === null ? l.unit_price : p;
  };
  const priceChanged = (l) => priceDrafts[keyOf(l)] !== undefined
    && parsePrice(priceDrafts[keyOf(l)]) !== null
    && parsePrice(priceDrafts[keyOf(l)]) !== l.unit_price;
  const lineCalc = (l, q) => {
    const gross = q * priceOf(l);
    const discount = (l.discount_amount * q / (l.quantity || 1)) + (gross * l.discount_percent / 100);
    const subtotal = Math.max(0, gross - discount);
    const tax = subtotal * l.tax_rate / 100;
    return { gross, discount, subtotal, tax, grand: subtotal + tax };
  };

  // Bu faturadaki tutar özeti — sipariş satırı snapshot'ından oransal (backend
  // invoice_order_shipped ile birebir aynı formül; kur/KDV/iskonto yeniden girilmez)
  const totals = useMemo(() => {
    const byCur = {};
    for (const l of lines) {
      const q = Number(qtys[keyOf(l)] || 0);
      if (q <= 0 || !l.quantity) continue;
      const c = lineCalc(l, q);
      const cur = byCur[l.currency] || { currency: l.currency, gross: 0, discount: 0, subtotal: 0, tax: 0, grand: 0 };
      cur.gross += c.gross;
      cur.discount += c.discount;
      cur.subtotal += c.subtotal;
      cur.tax += c.tax;
      cur.grand += c.grand;
      byCur[l.currency] = cur;
    }
    return Object.values(byCur);
  }, [lines, qtys, priceDrafts]);

  const setQty = (l, raw) => {
    const max = remainingOf(l);
    let v = parseInt(raw, 10);
    if (Number.isNaN(v)) v = 0;
    if (v > max) v = max;
    if (v < 0) v = 0;
    setQtys((prev) => ({ ...prev, [keyOf(l)]: v }));
  };

  const save = async () => {
    const items = lines
      .filter((l) => Number(qtys[keyOf(l)] || 0) > 0)
      .map((l) => ({
        line_id: l.id,
        qty: Number(qtys[keyOf(l)]),
        // Yalnız değiştirilen fiyatlar gönderilir → FT satırına yazılır, kaynak sipariş sabit kalır
        ...(priceChanged(l) ? { unit_price: priceOf(l) } : {}),
      }));
    if (!items.length) return toast.error("Faturalanacak miktar girin");
    // Vade < fatura tarihi engeli (backend 422 ile aynı mesaj)
    if (invDate && dueDate && dueDate < invDate) {
      return toast.error("Vade tarihi fatura tarihinden önce olamaz.");
    }
    const total = items.reduce((s, i) => s + i.qty, 0);
    if (!window.confirm(`${order.order_no} için ${items.length} kalem, toplam ${total} adet faturalanacak.\nKaynak siparişin aşaması değişmez; ayrı FT fatura belgesi oluşur.\nOnaylıyor musunuz?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/invoice`, {
        items,
        ...(invDate ? { invoice_date: invDate } : {}),
        ...(dueDate ? { due_date: dueDate } : {}),
      });
      toast.success(data?.message || "Fatura oluşturuldu");
      onOpenChange?.(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-5xl" data-testid="invoice-order-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-400" />
            Faturala
          </DialogTitle>
          {order && (
            <div className="text-xs text-slate-400 mt-1">
              <span className="font-mono text-cyan-400">{order.order_no}</span> · {order.customer_name}
              <span className="ml-2 text-slate-500">— kaynak siparişin aşaması değişmez, ayrı FT fatura oluşur</span>
            </div>
          )}
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3" data-testid="invoice-dates-row">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Fatura Tarihi</label>
            <Input
              type="date"
              data-testid="invoice-date-input"
              value={invDate}
              onChange={(e) => setInvDate(e.target.value)}
              className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Vade Tarihi</label>
            <Input
              type="date"
              data-testid="invoice-due-date-input"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs mt-1"
            />
          </div>
        </div>

        <div className="border border-slate-800 rounded-sm overflow-x-auto" data-testid="invoice-lines-scroll">
          <table className="w-full text-xs min-w-[860px]">
            <thead>
              <tr className="bg-slate-950 text-slate-500 uppercase tracking-wider font-mono text-[10px]">
                <th className="text-left px-3 py-2">Ürün</th>
                <th className="text-right px-2 py-2">Sevk</th>
                <th className="text-right px-2 py-2">Faturalanan</th>
                <th className="text-right px-2 py-2">Kalan</th>
                <th className="text-right px-2 py-2">Bu Faturada</th>
                <th className="text-right px-2 py-2">Birim Fiyat</th>
                <th className="text-right px-2 py-2">İskonto</th>
                <th className="text-right px-2 py-2">KDV %</th>
                <th className="text-right px-2 py-2">Tutar (KDV Hariç)</th>
                <th className="text-right px-3 py-2">Toplam (KDV Dahil)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {lines.map((l) => {
                const rem = remainingOf(l);
                const q = Number(qtys[keyOf(l)] ?? rem) || 0;
                const c = lineCalc(l, q);
                const flatPart = l.discount_amount > 0 ? l.discount_amount * q / (l.quantity || 1) : 0;
                return (
                  <tr key={keyOf(l)} data-testid={`invoice-line-${keyOf(l)}`}>
                    <td className="px-3 py-2">
                      <div className="text-slate-100 font-semibold">{l.name}</div>
                      {l.sku && <div className="text-[10px] font-mono text-slate-500">{l.sku}</div>}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-slate-200" data-testid={`invoice-shipped-${keyOf(l)}`}>{l.shipped}</td>
                    <td className="px-2 py-2 text-right font-mono text-slate-400" data-testid={`invoice-invoiced-${keyOf(l)}`}>{l.invoiced}</td>
                    <td className={`px-2 py-2 text-right font-mono font-semibold ${rem > 0 ? "text-amber-300" : "text-emerald-400"}`} data-testid={`invoice-remaining-${keyOf(l)}`}>{rem}</td>
                    <td className="px-2 py-2 text-right">
                      <Input
                        data-testid={`invoice-qty-${keyOf(l)}`}
                        type="number"
                        min={0}
                        max={rem}
                        disabled={rem <= 0}
                        value={qtys[keyOf(l)] ?? rem}
                        onChange={(e) => setQty(l, e.target.value)}
                        className="bg-slate-950 border-slate-700 rounded-sm h-7 w-16 text-xs font-mono text-right ml-auto"
                      />
                    </td>
                    <td className="px-2 py-2 text-right" data-testid={`invoice-price-${keyOf(l)}`}>
                      <div className="flex items-center justify-end gap-1">
                        <Input
                          data-testid={`invoice-price-input-${keyOf(l)}`}
                          inputMode="decimal"
                          disabled={rem <= 0}
                          value={priceDrafts[keyOf(l)] ?? String(l.unit_price)}
                          onChange={(e) => setPriceDrafts((p) => ({ ...p, [keyOf(l)]: e.target.value }))}
                          title={priceChanged(l) ? `Değiştirildi (sipariş: ${fmtN(l.unit_price)}) — yalnız bu FT faturaya uygulanır` : "Birim fiyat (yalnız bu faturaya uygulanır)"}
                          className={`bg-slate-950 rounded-sm h-7 w-24 text-xs font-mono text-right ml-auto ${
                            priceChanged(l) ? "border-amber-500 text-amber-200" : "border-slate-700"}`}
                        />
                        <span className="text-[10px] text-slate-500 font-mono w-8">{l.currency}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums" data-testid={`invoice-discount-${keyOf(l)}`}>
                      {l.discount_percent > 0 || l.discount_amount > 0 ? (
                        <div className="text-amber-300">
                          {l.discount_percent > 0 && <span>%{l.discount_percent}</span>}
                          {l.discount_percent > 0 && flatPart > 0 && <span className="text-slate-600"> + </span>}
                          {flatPart > 0 && <span>{fmtN(flatPart)} {l.currency}</span>}
                        </div>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-slate-300 tabular-nums" data-testid={`invoice-taxrate-${keyOf(l)}`}>
                      %{l.tax_rate}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-slate-100 tabular-nums" data-testid={`invoice-line-subtotal-${keyOf(l)}`}>
                      {fmtN(c.subtotal)} <span className="text-[10px] text-slate-500">{l.currency}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-cyan-300 tabular-nums" data-testid={`invoice-line-grand-${keyOf(l)}`}>
                      {fmtN(c.grand)} <span className="text-[10px] font-normal text-slate-500">{l.currency}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] font-mono text-slate-500">
          Toplam bu faturada: <span className="text-cyan-300 font-semibold" data-testid="invoice-total-qty">{totalQty}</span> adet
        </div>

        {totals.map((t) => (
          <div key={t.currency} className="border border-slate-800 rounded-sm bg-slate-950/40 px-3 py-2 space-y-1"
            data-testid={`invoice-totals-${t.currency}`}>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-slate-500 font-mono uppercase tracking-wider">Ara Toplam (KDV hariç)</span>
              <span className="font-mono text-slate-200 tabular-nums" data-testid={`invoice-subtotal-${t.currency}`}>
                {t.subtotal.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t.currency}
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-slate-500 font-mono uppercase tracking-wider">İskonto</span>
              <span className="font-mono text-amber-300 tabular-nums" data-testid={`invoice-discount-${t.currency}`}>
                -{t.discount.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t.currency}
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-slate-500 font-mono uppercase tracking-wider">KDV</span>
              <span className="font-mono text-slate-300 tabular-nums" data-testid={`invoice-tax-${t.currency}`}>
                {t.tax.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t.currency}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-800/60">
              <span className="text-slate-400 font-mono uppercase tracking-wider font-semibold">
                Genel Toplam <span className="text-cyan-400" data-testid={`invoice-currency-${t.currency}`}>({t.currency})</span>
              </span>
              <span className="font-mono text-cyan-300 font-bold tabular-nums" data-testid={`invoice-grand-${t.currency}`}>
                {t.grand.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t.currency}
              </span>
            </div>
          </div>
        ))}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            data-testid="invoice-cancel-btn"
            className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm"
          >
            İptal
          </Button>
          <Button
            data-testid="invoice-confirm-btn"
            onClick={save}
            disabled={saving || totalQty <= 0}
            className="bg-cyan-600 hover:bg-cyan-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Faturayı Kes ({totalQty})</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
